import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Checkbox, Divider, Grid, Group, Input, NumberInput, Select, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import type { ReactNode } from "react";
import { Fragment } from "react/jsx-runtime";
import type { Coach } from "../../../models/coach";
import { calculateGrade, getGradeOptionsType1 } from "../../../models/enums/grade";
import { ReportType } from "../../../models/enums/reportType";
import { syncReferenceArray } from "../../../models/IDElement";
import {
  type CertificateReport,
  type DisplayedElement,
  emptyDisplayElement,
  emptyReportSubject,
  type ReportProjectDetails,
} from "../../../models/reports/certificateReport";
import { BaseReportForm } from "./BaseReportForm";
import type { CommonReportFormType } from "./CommonReportForm";

type CertificateReportFormProps = {
  certificateReport: CertificateReport;
  saveButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
  coaches: Coach[];
};

type CertificateReportFormType = CommonReportFormType & {
  data: {
    uek_modules: DisplayedElement[];
    technical_education: DisplayedElement[];
    project_details?: ReportProjectDetails;
  };
};

type FormErrors = {
  title?: string[];
  status?: string[];
  data?: string[];
};

export function CertificateReportForm({ certificateReport, saveButtonLabel, onSubmit, coaches }: CertificateReportFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;
  const hasProject =
    certificateReport.report_configuration.report_type === ReportType.CERT && certificateReport.report_configuration.data.project;
  const syncedUekModules = syncReferenceArray(
    emptyDisplayElement,
    certificateReport.report_configuration.data.uek_modules,
    certificateReport.data.uek_modules,
  );
  const syncedTechnicalEducation = syncReferenceArray(
    emptyDisplayElement,
    certificateReport.report_configuration.data.technical_education,
    certificateReport.data.technical_education,
  );
  const syncedSubjects = syncReferenceArray(
    emptyReportSubject,
    certificateReport.report_configuration.data.project_details?.subject,
    certificateReport.data.project_details?.subject,
  );

  const form = useForm<CertificateReportFormType>({
    mode: "uncontrolled",
    initialValues: {
      title: certificateReport.title,
      status: certificateReport.status,
      responsible_coach_id: certificateReport.coach.id.toString(),
      data: {
        uek_modules: syncedUekModules,
        technical_education: syncedTechnicalEducation,
        project_details: {
          title: certificateReport.data.project_details?.title ?? "",
          total_archived_points: certificateReport.data.project_details?.total_archived_points ?? null,
          total_grade: certificateReport.data.project_details?.total_grade ?? null,
          subject: syncedSubjects,
        },
      },
    },
  });

  const calculateProjectGrade = () => {
    const total = form.getValues().data.project_details?.subject.reduce((prev, curr) => prev + (curr.archived_points ?? 0), 0);
    form.setFieldValue("data.project_details.total_archived_points", total);
    form.setFieldValue(
      "data.project_details.total_grade",
      calculateGrade(total ?? 0, certificateReport.report_configuration.data.project_details.total_max_points ?? 0),
    );
  };

  const uekModuleInputs = certificateReport.report_configuration.data.uek_modules.map((uek, index) => (
    <Fragment key={uek.id}>
      <Group>
        <Text>{uek.num}</Text>
        <Text>-</Text>
        <Text flex={1}>{uek.name}</Text>
        <Checkbox
          label="Anzeigen"
          key={form.key(`data.uek_modules.${index}.displayed`)}
          {...form.getInputProps(`data.uek_modules.${index}.displayed`, { type: "checkbox" })}
        />
      </Group>
      <Divider />
    </Fragment>
  ));

  const technicalEducationInputs = certificateReport.report_configuration.data.technical_education.map((technical, index) => (
    <Fragment key={technical.id}>
      <Group>
        <Text flex={1}>
          {technical.title} {technical.details}
        </Text>
        <Checkbox
          label="Anzeigen"
          key={form.key(`data.technical_education.${index}.displayed`)}
          {...form.getInputProps(`data.technical_education.${index}.displayed`, { type: "checkbox" })}
        />
      </Group>
      <Divider />
    </Fragment>
  ));

  let projectDetails: ReactNode[] | undefined = [];
  if (hasProject) {
    projectDetails = certificateReport.report_configuration.data.project_details?.subject?.map((subject, index) => (
      <Fragment key={subject.id}>
        <Grid.Col span={6}>
          <Text>{subject.title}</Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            flex={1}
            min={0}
            max={subject.max_points ?? undefined}
            placeholder="Erreichte Punkte"
            key={form.key(`data.project_details.subject.${index}.archived_points`)}
            {...form.getInputProps(`data.project_details.subject.${index}.archived_points`)}
            onBlur={(event) => {
              form.getInputProps(`data.project_details.subject.${index}.archived_points`).onBlur(event);
              calculateProjectGrade();
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Text ta="right" flex={1}>
            {subject.max_points}
          </Text>
        </Grid.Col>
      </Fragment>
    ));
  }

  return (
    <BaseReportForm
      form={form}
      errors={errors}
      report={certificateReport}
      coaches={coaches}
      onSubmit={form.onSubmit(onSubmit)}
      saveButtonLabel={saveButtonLabel}
    >
      <Input.Label>ÜK Module</Input.Label>
      {uekModuleInputs}
      <Input.Label>Fachliche Ausbildung</Input.Label>
      {technicalEducationInputs}

      {hasProject && (
        <>
          <Input.Label>Projekt</Input.Label>
          <TextInput
            label="Projekt Titel"
            key={form.key("data.project_details.title")}
            {...form.getInputProps("data.project_details.title")}
          />
          <Grid>
            <Grid.Col span={6}>
              <Input.Label>Bereich</Input.Label>
            </Grid.Col>
            <Grid.Col span={4}>
              <Input.Label>Erreichte Punkte</Input.Label>
            </Grid.Col>
            <Grid.Col span={2} ta="right">
              <Input.Label>Totale Punkte</Input.Label>
            </Grid.Col>
            {projectDetails}
          </Grid>
          <Group>
            <NumberInput
              disabled
              flex={1}
              min={0}
              label={`Erreichte Punkte (max ${certificateReport.report_configuration.data.project_details?.total_max_points})`}
              max={certificateReport.report_configuration.data.project_details?.total_max_points ?? undefined}
              key={form.key("data.project_details.total_archived_points")}
              {...form.getInputProps("data.project_details.total_archived_points")}
            />
            <Select
              disabled
              flex={1}
              label="Gesamtbewertung"
              data={getGradeOptionsType1()}
              key={form.key("data.project_details.total_grade")}
              {...form.getInputProps("data.project_details.total_grade")}
            />
          </Group>
        </>
      )}
    </BaseReportForm>
  );
}
